import { Box } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useRef } from 'react';
import { matchPath, useLocation, useMatch, useNavigate } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';

import App from './App';
import { useAuth } from './components/auth/authProvider';
import { useInviteToken } from './components/auth/useInviteToken';
import getConfigValue from './config';
import { useAppSelector } from './hooks';
import { useGetAccountQuery } from './services/accounts';
import { useGetIntercomUserJwtQuery } from './services/auth';
import { setDataDogUser } from './services/datadog';
import {
  disableIntercomWidgetWithRetry,
  haveIntercomAudienceAttributesChanged,
  type IntercomAudienceAttributes,
  useIntercomReboot,
} from './services/intercomReboot';

function AuthenticatingWrapper() {
  const { shouldLogout } = useAppSelector((state) => state.auth);
  const { showRateLimitBanner } = useAppSelector((state) => state.rateLimit);
  const { login, logout, isAuthenticated, user, claims, homeOrganizationData } = useAuth();
  const intercomAppId = getConfigValue('VITE_APP_INTERCOM_ID') || '';
  const { hardShutdown, update: updateIntercom } = useIntercom();
  const rebootIntercom = useIntercomReboot();
  const hasBooted = useRef(false);
  const lastAudienceAttributes = useRef<IntercomAudienceAttributes | null>(null);
  // Tracks the expiresAt we've already triggered a pre-expiry refetch for, so refetch fires
  // at most once per token cycle (syncIntercom re-runs on many dep changes).
  const pendingRefetchForToken = useRef<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const accountMatch = useMatch('/accounts/:id/*');
  const accountId = accountMatch?.params.id;
  const { isInviteToken } = useInviteToken();
  const {
    data: account,
    isLoading: isAccountLoading,
    isError,
    error,
  } = useGetAccountQuery(accountId as string, {
    skip: !accountId || isInviteToken || !isAuthenticated,
  });

  // Fetch the Intercom messenger JWT for identity verification. Skip when
  // unauthenticated or on invite-token routes where Intercom is disabled.
  const {
    data: intercomJwtData,
    isError: isIntercomJwtError,
    error: intercomJwtError,
    refetch: refetchIntercomJwt,
  } = useGetIntercomUserJwtQuery(undefined, {
    // syncIntercom only boots when email + sub are present, so skip the JWT fetch
    // whenever either is missing (avoids a wasted /api/auth/intercom-user-jwt call for
    // authenticated-but-email-less identities that will never boot the messenger).
    skip: !isAuthenticated || !user?.email || !user?.sub || isInviteToken,
  });

  const isNotAuthenticating = !isAuthenticated && !isInviteToken;

  useEffect(() => {
    if (isAuthenticated && location?.pathname === '/') {
      navigate('/accounts');
    } else if (isNotAuthenticating && !showRateLimitBanner) {
      login();
    }
    setDataDogUser({ id: user?.sub, username: user?.name, email: user?.email, organization: claims?.org });
  }, [isAuthenticated, isNotAuthenticating, login, navigate, location, user, claims?.org, showRateLimitBanner]);

  useEffect(() => {
    if (shouldLogout) {
      logout();
    }
  }, [logout, shouldLogout]);

  const syncIntercom = useCallback(() => {
    if (isInviteToken) {
      const cancelIntercomDisableRetry = disableIntercomWidgetWithRetry(hardShutdown);
      hasBooted.current = false;
      lastAudienceAttributes.current = null;
      return cancelIntercomDisableRetry;
    }

    if (!intercomAppId) {
      return;
    }

    if (!isAuthenticated || !user?.email || !user?.sub || !homeOrganizationData) return;

    // Wait for the Intercom JWT unless the fetch errored. On error we degrade
    // gracefully: log loudly and boot without identity verification so the
    // messenger still works (anonymous boot is Intercom's defined failure mode).
    if (isIntercomJwtError) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch Intercom user JWT — booting without identity verification:', intercomJwtError);
    } else if (!intercomJwtData?.token) {
      // Still loading — suppress boot until data (or error) lands.
      return;
    }

    const isAccountPage = matchPath({ path: '/accounts/:id/*' }, location?.pathname || '');

    // On account pages, wait for account data before booting/updating
    if (isAccountPage && isAccountLoading) return;
    if (isAccountPage && !account && !isError) return;

    if (isError) {
      // eslint-disable-next-line no-console
      console.error('Failed to load account for Intercom:', error);
    }

    const activePageProgram = isAccountPage && account ? (account.programType ?? null) : null;
    const pageState = isAccountPage && account ? (account.address?.state ?? null) : null;

    // active_account_id is intentionally excluded from audienceAttributes.
    // Including it there would cause haveIntercomAudienceAttributesChanged to
    // return true on every account page navigation, triggering a full messenger
    // reboot (hardShutdown + reload) instead of the lightweight update() path.
    const activeAccountId = isAccountPage && accountId ? accountId : null;

    const customAttributes = {
      active_page_program: activePageProgram,
      state: pageState,
    };
    const companyCustomAttributes = {
      programTypes: homeOrganizationData?.programTypes?.join(', '),
    };
    const audienceAttributes = {
      userCustomAttributes: customAttributes,
      companyCustomAttributes,
    };

    // Token-refresh guard: when the cached token is within 60s of expiry, proactively refetch
    // so the NEXT cycle has a fresh token (refetch() triggers an RTK data change, which re-runs
    // this effect — no setInterval needed). The current token is still cryptographically valid,
    // so keep attaching it this cycle; only drop it once it has actually expired, where
    // attaching it would boot an invalid identity. This avoids an unnecessary unverified boot
    // in the common aging-but-still-valid case.
    let resolvedToken: string | undefined = intercomJwtData?.token;
    if (resolvedToken && intercomJwtData?.expiresAt) {
      // Luxon (project UTC convention). A malformed expiresAt is invalid -> treat as already
      // expired so we refetch and drop the unusable token rather than NaN-comparing.
      const expiry = DateTime.fromISO(intercomJwtData.expiresAt);
      const msUntilExpiry = expiry.isValid ? expiry.diffNow().toMillis() : -1;
      if (msUntilExpiry < 60_000) {
        // Refetch at most once per token cycle: RTK refetch() forces a request (no in-flight
        // dedup), and syncIntercom re-runs on many dep changes, so an unguarded call would fire
        // a refetch storm at /api/auth/intercom-user-jwt during the pre-expiry window.
        if (pendingRefetchForToken.current !== intercomJwtData.expiresAt) {
          pendingRefetchForToken.current = intercomJwtData.expiresAt;
          refetchIntercomJwt();
        }
      }
      if (msUntilExpiry <= 0) {
        resolvedToken = undefined;
      }
    }

    const userData = {
      // userId (Auth0 sub) is sent alongside the JWT so Intercom can match the
      // contact record. The JWT payload also contains user_id but the boot prop
      // is the declarative signal that triggers identity verification.
      // user.sub is guaranteed non-nullish by the guard above (user?.sub check).
      //
      // Degradation when intercomUserJwt is absent (fetch error, or pre-Phase-0):
      //   - Before JWT enforcement (current prod): Intercom accepts the email-keyed
      //     identity and the messenger boots identified, exactly as it does today.
      //   - After enforcement: Intercom rejects the unverified identity and serves an
      //     anonymous messenger - degraded but still functional, never a blank widget.
      // We cannot detect enforcement client-side, so this single identified-boot path
      // is correct for both phases; the Phase-0 runbook gates enforcement on a healthy
      // JWT endpoint so the post-enforcement error case is an outage edge only.
      userId: user.sub,
      // Omitted rather than sent empty when absent: Intercom leaves an attribute it does not receive
      // untouched, but treats a present-but-empty one as an instruction to clear the stored value.
      // Auth0 does not require a profile name and organizations need not carry a phone number, so
      // both fallbacks are reachable, and neither has a value worth writing when it fires.
      ...(user?.name && { name: user.name }),
      email: user.email,
      ...(homeOrganizationData?.phoneNumber && { phone: homeOrganizationData.phoneNumber }),
      ...(resolvedToken ? { intercomUserJwt: resolvedToken } : {}),
      company: {
        companyId: homeOrganizationData?.alias,
        name: homeOrganizationData?.name,
        customAttributes: companyCustomAttributes,
      },
      customAttributes: {
        ...customAttributes,
        // Placed in customAttributes only — NOT in audienceAttributes — so
        // navigation between account pages never triggers a reboot.
        active_account_id: activeAccountId,
      },
    };

    const audienceAttrsChanged = haveIntercomAudienceAttributesChanged(
      lastAudienceAttributes.current,
      audienceAttributes,
    );

    if (!hasBooted.current || audienceAttrsChanged) {
      rebootIntercom(intercomAppId, userData);
      hasBooted.current = true;
    } else {
      updateIntercom(userData);
    }

    lastAudienceAttributes.current = audienceAttributes;
  }, [
    isInviteToken,
    hardShutdown,
    intercomAppId,
    isAuthenticated,
    user,
    homeOrganizationData,
    intercomJwtData,
    isIntercomJwtError,
    intercomJwtError,
    refetchIntercomJwt,
    account,
    accountId,
    isAccountLoading,
    isError,
    error,
    location,
    rebootIntercom,
    updateIntercom,
  ]);

  useEffect(() => {
    return syncIntercom();
  }, [syncIntercom]);

  if (isNotAuthenticating) {
    return (
      <Box
        alignItems="center"
        background="secondary"
        color="body-primary"
        fontSize="sm"
        height="100vh"
        justifyContent="center"
        padding="lg"
      >
        Redirecting to log in page...
      </Box>
    );
  }

  return <App />;
}

export default AuthenticatingWrapper;
